import { useParams, Link, Navigate } from "react-router-dom";
import { AlertTriangle, Home as HomeIcon } from "lucide-react";
import { LEGAL_PAGES } from "@/lib/site-data";
import useSeo from "@/lib/useSeo";

const PLACEHOLDERS = (
  <div
    className="ep-card p-4 my-6 border-[#00F22E]/30"
    data-testid="legal-placeholder-notice"
  >
    <div className="flex items-start gap-3">
      <AlertTriangle className="h-5 w-5 text-[#00F22E] mt-0.5" />
      <div className="text-sm text-white/90 leading-relaxed">
        <p className="font-semibold text-white mb-1">Bu metin bir şablondur.</p>
        <p className="text-muted">
          Aşağıdaki alanlar yayınlanmadan önce yetkili hukuk danışmanı
          tarafından tamamlanmalı ve gözden geçirilmelidir: [VERİ SORUMLUSU RESMÎ
          ADI], [YASAL TEBLİGAT ADRESİ], [VERGİ BİLGİSİ], [KEP ADRESİ VARSA],
          [POLİTİKA YÜRÜRLÜK TARİHİ].
        </p>
      </div>
    </div>
  </div>
);

const TEMPLATES = {
  kvkk: {
    title: "KVKK Aydınlatma Metni",
    body: [
      "İşbu Aydınlatma Metni, 6698 sayılı Kişisel Verilerin Korunması Kanunu (\"KVKK\") kapsamında, [VERİ SORUMLUSU RESMÎ ADI] tarafından veri sorumlusu sıfatıyla düzenlenmiştir.",
      "Kişisel verileriniz; ücretsiz analiz başvurusu, hizmet talebi ve iletişim süreçlerinin yürütülmesi amacıyla, sizin tarafınızdan doldurulan formlar ve iletişim kanalları aracılığıyla toplanmaktadır.",
      "İşlenen kişisel verileriniz: ad soyad, telefon, e-posta, Üye İşyeri bilgileri, il/ilçe, sektör bilgileri ve tarafınızca formda paylaşılan diğer bilgilerdir.",
      "Verileriniz; başvurunuzun değerlendirilmesi, tarafınızla iletişime geçilmesi ve ilgili yasal yükümlülüklerin yerine getirilmesi amaçlarıyla, gerekli teknik ve idari tedbirler alınarak işlenir.",
      "KVKK'nın 11. maddesi kapsamındaki haklarınızı kullanmak için taleplerinizi [YASAL TEBLİGAT ADRESİ] veya [KEP ADRESİ VARSA] üzerinden iletebilirsiniz.",
    ],
  },
  "gizlilik-politikasi": {
    title: "Gizlilik Politikası",
    body: [
      "İşbu Gizlilik Politikası; Epersonel web sitesini ziyaret eden ve/veya form dolduran kullanıcıların bilgilerinin nasıl işlendiğine ilişkin genel bilgileri içerir. Politika'nın yasal içeriği [VERİ SORUMLUSU RESMÎ ADI] tarafından [POLİTİKA YÜRÜRLÜK TARİHİ] itibarıyla belirlenecektir.",
      "Kullanıcı tarafından formlar aracılığıyla iletilen kişisel veriler yalnızca ilgili başvurunun ve hizmet sürecinin yürütülmesi amacıyla kullanılır.",
      "Kişisel veriler, yasal zorunluluk hâlleri saklı kalmak kaydıyla üçüncü kişilerle paylaşılmaz.",
      "Site üzerinde kullanılan analitik ve pazarlama araçları, yalnızca kullanıcı onayı sonrasında etkinleştirilir.",
    ],
  },
  "cerez-politikasi": {
    title: "Çerez Politikası",
    body: [
      "Web sitemizde; sitenin temel işlevlerinin sağlanması, kullanıcı deneyiminin iyileştirilmesi ve pazarlama iletişiminin ölçülmesi amacıyla çerezler kullanılabilir.",
      "Zorunlu Çerezler: Sitenin çalışması için gereklidir ve devre dışı bırakılamaz.",
      "Analitik Çerezler: Site kullanımını anlamamıza ve deneyimi iyileştirmemize yardımcı olur. Yalnızca onayınızla etkinleştirilir.",
      "Pazarlama Çerezleri: Reklam etkinliğini ölçmek amacıyla kullanılabilir. Yalnızca onayınızla etkinleştirilir.",
      "Tercihlerinizi istediğiniz zaman \"Çerez Tercihleri\" bağlantısı üzerinden güncelleyebilirsiniz.",
    ],
  },
  "kullanim-kosullari": {
    title: "Kullanım Koşulları",
    body: [
      "İşbu Kullanım Koşulları; Epersonel web sitesinin kullanımına ilişkin genel esasları düzenler. Yasal içeriği [VERİ SORUMLUSU RESMÎ ADI] tarafından tamamlanacaktır.",
      "Web sitesindeki tüm içerik yalnızca bilgilendirme amacıyla sunulmaktadır ve önceden bildirim yapılmaksızın değiştirilebilir.",
      "Web sitesinde yer alan marka, logo ve içerikler ilgili hak sahiplerine aittir. İzinsiz kullanılamaz.",
      "Bu koşullardan doğabilecek uyuşmazlıklarda [VERİ SORUMLUSU RESMÎ ADI] tarafından belirlenecek yetkili mahkemeler yetkilidir.",
    ],
  },
  "ticari-iletisim-izni": {
    title: "Ticari İletişim İzni",
    body: [
      "6563 sayılı Elektronik Ticaretin Düzenlenmesi Hakkında Kanun kapsamında, kampanya, tanıtım ve bilgilendirme amacıyla tarafınıza elektronik iletişim gönderilebilmesi için açık rızanız aranmaktadır.",
      "Ticari İletişim İzni; ücretsiz analiz başvurusu sırasında ayrı ve isteğe bağlı bir onay kutusu ile alınır.",
      "İzninizi istediğiniz zaman iletişim@ tarafından güncelleyebilir veya iptal edebilirsiniz. İptal talebinizi [YASAL TEBLİGAT ADRESİ] veya [KEP ADRESİ VARSA] üzerinden gönderebilirsiniz.",
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const item = TEMPLATES[slug];
  const meta = LEGAL_PAGES.find((l) => l.slug === slug);
  useSeo({
    title: item ? `${item.title} | Epersonel` : "Yasal | Epersonel",
    description: item
      ? `${item.title} — Epersonel kurumsal yasal metinleri.`
      : "Epersonel kurumsal yasal metinleri.",
  });
  if (!item || !meta) return <Navigate to="/" replace />;

  return (
    <section className="pt-16 pb-24">
      <div className="ep-container max-w-3xl">
        <nav className="flex items-center gap-2 text-xs text-muted mb-8" aria-label="Sayfa Yolu">
          <Link to="/" className="hover:text-[#00F22E] inline-flex items-center gap-1">
            <HomeIcon className="h-3 w-3" /> Ana Sayfa
          </Link>
          <span>/</span>
          <span className="text-white/80">{item.title}</span>
        </nav>
        <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight">
          {item.title}
        </h1>
        {PLACEHOLDERS}
        <div className="mt-6 space-y-4 text-muted leading-relaxed">
          {item.body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
        <div className="mt-10 flex gap-3">
          <Link to="/iletisim" className="btn-secondary">
            İletişime Geçin
          </Link>
        </div>
      </div>
    </section>
  );
}
